import type { AnyExtension, Content, Extension } from "@tiptap/core";
import { Document } from "@tiptap/extension-document";
import { Paragraph } from "@tiptap/extension-paragraph";
import { Placeholder } from "@tiptap/extension-placeholder";
import { Text } from "@tiptap/extension-text";
import { EditorContent, type UseEditorOptions, useEditor } from "@tiptap/react";
import classNames from "classnames";
import { useEffect, useRef } from "react";
import {
    getTextContentWithoutNewlines,
    stringContentToJSON,
} from "../../utils/tiptap.util";

import "./TiptapEditor.css";

const extensions: AnyExtension[] = [Document, Paragraph, Text];

export function TiptapEditor({
    label,
    placeholder,
    isValid,
    disabled,
    content,
    supportingText,
    onChange,
    onBlur,
    ...props
}: Omit<UseEditorOptions, "onBlur" | "onUpdate"> & {
    label?: string;
    placeholder?: string;
    isValid?: boolean | ((value?: Content) => boolean);
    disabled?: boolean;
    supportingText?: string;
    onChange?: (content: string) => void;
    onBlur?: (content: string) => void;
}) {
    const currentValue = useRef(content);
    const placeholderExtension: Extension[] = placeholder
        ? [
              Placeholder.configure({
                  placeholder,
              }),
          ]
        : [];

    const labelId = label
        ? `tiptap-editor-${label?.toLowerCase().replace(/\s+/g, "-")}`
        : undefined;

    const transformedContent =
        typeof content === "string" ? stringContentToJSON(content) : content;

    const isInvalid =
        typeof isValid === "boolean"
            ? !isValid
            : typeof isValid === "function"
              ? !isValid(currentValue.current)
              : false;

    const editor = useEditor({
        editable: !disabled,
        extensions: extensions.concat(placeholderExtension),
        content: transformedContent,
        editorProps: {
            attributes: {
                class: classNames("tiptap-editor", {
                    "tiptap-editor--invalid": isInvalid,
                    "tiptap-editor--disabled": disabled,
                }),
                "aria-invalid": isInvalid.toString(),
                "aria-disabled": disabled ? "true" : "false",
                id: labelId ?? "",
                role: "textbox",
                "aria-multiline": "true",
            },
        },
        onUpdate: ({ editor }) => {
            const value = getTextContentWithoutNewlines(editor);

            currentValue.current = value;

            onChange?.(value);
        },
        onBlur: ({ editor }) => {
            if (!onBlur) {
                return;
            }

            const value = getTextContentWithoutNewlines(editor);

            onBlur(value);
        },
        ...props,
    });

    useEffect(
        function updateInitialContent() {
            if (typeof content === "string") {
                editor?.commands.setContent(stringContentToJSON(content));
            }
        },

        [content],
    );

    return (
        <div className="tiptap-editor__wrapper">
            {label && (
                <label
                    className={classNames("tiptap-editor__label", {
                        "tiptap-editor__label--disabled": disabled,
                    })}
                    htmlFor={labelId}
                >
                    {label}
                </label>
            )}
            <EditorContent editor={editor} />

            {supportingText && (
                <p className="tiptap-editor__supporting-text">{supportingText}</p>
            )}
        </div>
    );
}
